"use client";

import { cn, formatPercentage } from "@drpcrd/utils";
import { Typography } from "./typography";

type StatsItem = {
  label: string;
  value: number;
};

type Props = {
  data: StatsItem[];
  /** How many entries of the data will be displayed */
  maxDisplayedData: number;
  className?: string;
  title?: string;
  showPercentage?: boolean;
  valueFormatter?: (value: number) => string;
  totalValue?: number;
};

/**
 * A simple stats list component that displays label-value pairs
 */
export const StatsList = ({
  data,
  className,
  title,
  showPercentage = true,
  valueFormatter,
  totalValue,
  maxDisplayedData,
}: Props) => {
  // Calculate the sum of all values to use for percentage calculation
  const sum = totalValue ?? data.reduce((acc, item) => acc + item.value, 0);

  // Transform data to include percentages + Sort + Limit items
  const displayData = data
    .map((item) => ({
      ...item,
      originalValue: item.value,
      value:
        sum > 0
          ? showPercentage
            ? Number((item.value / sum).toFixed(2))
            : item.value
          : 0,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, maxDisplayedData);

  const formatValue = (value: number): string => {
    if (valueFormatter) {
      return valueFormatter(value);
    } else if (showPercentage) {
      return formatPercentage(value);
    } else {
      return value.toString();
    }
  };

  return (
    <div className={cn("flex w-full flex-col gap-2", className)}>
      {title && (
        <Typography weight="semibold" size="sm">
          {title}
        </Typography>
      )}
      <ul className="flex w-full flex-col gap-0">
        {displayData.map((item) => (
          <li
            key={item.label}
            className="flex w-full items-center justify-end" // Always right aligned
          >
            <Typography>
              <span className="font-semibold">{item.label}</span>
              {" · "}
              {formatValue(item.value)}
            </Typography>
          </li>
        ))}
      </ul>
    </div>
  );
};
